import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiClock,
  FiCalendar,
  FiUsers,
  FiAward,
  FiBookOpen,
  FiHeart,
  FiCheckCircle,
  FiStar,
  FiArrowRight,
} from "react-icons/fi";
import { BsStars } from "react-icons/bs";

// Import images
import heroImage from "../assets/schulungen/schu1.JPEG";
import detailImage1 from "../assets/schulungen/schu2.JPEG";
import detailImage2 from "../assets/schulungen/schu3.JPEG";
import detailImage3 from "../assets/schulungen/schu4.JPEG";

// --- Styled Components ---
const PageContainer = styled.div`
  min-height: 100vh;
  background: #fcf9f7;
`;

// Hero Section
const HeroSection = styled.section`
  height: 70vh;
  min-height: 600px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const HeroImage = styled(motion.div)`
  position: absolute;
  inset: 0;
  background-image: url(${heroImage});
  background-size: cover;
  background-position: center;
  filter: brightness(0.7) blur(15px);
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(99, 86, 78, 0.4) 0%,
    rgba(142, 116, 121, 0.3) 100%
  );
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 900px;
  padding: 0 20px;
`;

const HeroTitle = styled(motion.h1)`
  font-family: "Lovelace", serif;
  font-size: clamp(3rem, 8vw, 6rem);
  font-weight: 400;
  margin: 0 0 20px;
  letter-spacing: -1px;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);
`;

const HeroSubtitle = styled(motion.p)`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.5rem;
  font-style: italic;
  opacity: 0.95;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const HeroStats = styled(motion.div)`
  display: flex;
  gap: 50px;
  justify-content: center;
  margin-top: 50px;

  @media (max-width: 640px) {
    gap: 25px;
    flex-wrap: wrap;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const StatNumber = styled.span`
  font-family: "Lovelace", serif;
  font-size: 2rem;
  font-weight: 400;
`;

const StatLabel = styled.span`
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  opacity: 0.8;
`;

// What To Expect Section (NEW)
const ExpectSection = styled.section`
  padding: 120px 20px;
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const ExpectContent = styled(motion.div)`
  h2 {
    font-family: "Lovelace", serif;
    font-size: 3.5rem;
    color: #63564e;
    margin: 0 0 30px;
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: -1px;
  }

  p {
    color: #8e7479;
    line-height: 1.9;
    margin-bottom: 25px;
    font-size: 1.1rem;
  }
`;

const ExpectImageWrapper = styled(motion.div)`
  position: relative;
  height: 600px;
`;

const ExpectMainImage = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 40px;
  overflow: hidden;
  background-image: url(${detailImage3});
  background-size: cover;
  background-position: center;
  box-shadow: 0 40px 80px rgba(99, 86, 78, 0.25);
`;

const ExpectAccentImage = styled(motion.div)`
  position: absolute;
  bottom: -40px;
  left: -40px;
  width: 200px;
  height: 200px;
  border-radius: 30px;
  overflow: hidden;
  background-image: url(${detailImage2});
  background-size: cover;
  background-position: center;
  border: 4px solid white;
  box-shadow: 0 20px 40px rgba(99, 86, 78, 0.2);
`;

const SupportNote = styled(motion.div)`
  margin-top: 40px;
  padding: 25px 30px;
  background: white;
  border-radius: 60px;
  display: inline-flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 10px 30px rgba(155, 128, 103, 0.1);
  border: 1px solid rgba(155, 128, 103, 0.1);

  svg {
    color: #9b8067;
    font-size: 1.8rem;
  }

  span {
    color: #63564e;
    font-size: 1.1rem;
    font-style: italic;
  }
`;

// Introduction Section
const IntroSection = styled.section`
  padding: 100px 20px;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const IntroContent = styled(motion.div)`
  h2 {
    font-family: "Lovelace", serif;
    font-size: 2.8rem;
    color: #63564e;
    margin: 0 0 20px;
    font-weight: 400;
    line-height: 1.2;
  }

  p {
    color: #8e7479;
    line-height: 1.8;
    margin-bottom: 25px;
    font-size: 1.1rem;
  }
`;

const IntroImage = styled(motion.div)`
  height: 500px;
  border-radius: 30px;
  overflow: hidden;
  background-image: url(${detailImage1});
  background-size: cover;
  background-position: center;
  box-shadow: 0 30px 60px rgba(99, 86, 78, 0.15);
`;

const FeatureList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 30px;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #63564e;

  svg {
    color: #9b8067;
    font-size: 1.2rem;
  }
`;

// Trainings Section
// Trainings Section
const TrainingsSection = styled.section`
  padding: 80px 20px 120px;
  background: white;
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  max-width: 600px;
  margin: 0 auto 80px;

  h2 {
    font-family: "Lovelace", serif;
    font-size: 3rem;
    color: #63564e;
    margin: 0 0 15px;
    font-weight: 400;

    @media (max-width: 500px) {
      font-size: 2.2rem;
    }
  }

  p {
    color: #8e7479;
    font-size: 1.2rem;
    font-style: italic;

    @media (max-width: 500px) {
      font-size: 1rem;
      padding: 0 10px;
    }
  }
`;

const TrainingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  max-width: 1300px;
  margin: 0 auto;

  @media (max-width: 898px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  @media (max-width: 500px) {
    gap: 20px;
    padding: 0 10px;
  }
`;

const TrainingCard = styled(motion.div)`
  background: #fcf9f7;
  border-radius: 40px;
  padding: 40px;
  border: 1px solid rgba(155, 128, 103, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 30px 60px rgba(99, 86, 78, 0.08);
  }

  @media (max-width: 900px) {
    width: 100%;
  }

  @media (max-width: 500px) {
    padding: 25px 20px;
    border-radius: 30px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;

  @media (max-width: 500px) {
    flex-direction: column;
    gap: 15px;
    margin-bottom: 20px;
  }
`;

const CardTitle = styled.h3`
  font-family: "Lovelace", serif;
  font-size: 2rem;
  color: #63564e;
  margin: 0;
  font-weight: 400;
  line-height: 1.2;

  @media (max-width: 500px) {
    font-size: 1.8rem;
  }
`;

const CardBadge = styled.span`
  background: ${(props) => props.color || "#9B8067"};
  color: white;
  padding: 6px 15px;
  border-radius: 30px;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  @media (max-width: 500px) {
    align-self: flex-start;
    font-size: 0.7rem;
    padding: 5px 12px;
  }
`;

const CardDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 25px 0;
  border-top: 1px solid rgba(155, 128, 103, 0.2);
  border-bottom: 1px solid rgba(155, 128, 103, 0.2);
  margin: 25px 0;

  @media (max-width: 500px) {
    gap: 10px;
    padding: 20px 0;
    margin: 20px 0;
  }
`;

const Detail = styled.div`
  text-align: center;

  svg {
    color: #9b8067;
    font-size: 1.5rem;
    margin-bottom: 8px;

    @media (max-width: 500px) {
      font-size: 1.2rem;
      margin-bottom: 5px;
    }
  }

  div:first-of-type {
    font-family: "Lovelace", serif;
    font-size: 1.5rem;
    color: #63564e;
    line-height: 1.2;

    @media (max-width: 500px) {
      font-size: 1.2rem;
    }
  }

  span {
    font-size: 0.8rem;
    color: #8e7479;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (max-width: 500px) {
      font-size: 0.7rem;
    }
  }
`;

const IncludesList = styled.div`
  margin: 25px 0;

  @media (max-width: 500px) {
    margin: 20px 0;
  }
`;

const IncludesTitle = styled.div`
  font-weight: 500;
  color: #63564e;
  margin-bottom: 15px;
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;

  @media (max-width: 500px) {
    font-size: 0.8rem;
    margin-bottom: 12px;
  }
`;

const IncludesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const IncludeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #5d4e4a;
  font-size: 0.9rem;

  svg {
    color: #9b8067;
    font-size: 1rem;
    flex-shrink: 0;
  }

  @media (max-width: 500px) {
    font-size: 0.85rem;
    gap: 6px;
  }
`;

const CardFooter = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 30px;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    margin-top: 20px;
  }
`;

const Price = styled.div`
  font-family: "Lovelace", serif;
  font-size: 2.5rem;
  color: #9b8067;
  line-height: 1;

  @media (max-width: 500px) {
    font-size: 2rem;
  }
`;

const PriceNote = styled.div`
  color: #8e7479;
  font-size: 0.8rem;
  margin-top: 5px;

  @media (max-width: 500px) {
    font-size: 0.75rem;
  }
`;

const BookButton = styled(motion.button)`
  background: transparent;
  border: 1px solid #9b8067;
  color: #9b8067;
  padding: 12px 30px;
  border-radius: 40px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: #9b8067;
    color: white;
  }

  @media (max-width: 500px) {
    width: 100%;
    justify-content: center;
    padding: 15px 30px;
    font-size: 0.85rem;
  }
`;

// Why Choose Us Section
const WhySection = styled.section`
  padding: 100px 20px;
  background: linear-gradient(135deg, #f5f0eb 0%, #faf5f0 100%);
  position: relative;
  overflow: hidden;
`;

const WhyContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const WhyImage = styled(motion.div)`
  height: 600px;
  border-radius: 40px;
  overflow: hidden;
  background-image: url(${detailImage2});
  background-size: cover;
  background-position: center;
  box-shadow: 0 40px 80px rgba(99, 86, 78, 0.2);
`;

const WhyList = styled.div`
  display: grid;
  gap: 40px;
`;

const WhyItem = styled(motion.div)`
  display: flex;
  gap: 20px;
`;

const WhyIcon = styled.div`
  width: 60px;
  height: 60px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9b8067;
  font-size: 1.5rem;
  box-shadow: 0 10px 20px rgba(155, 128, 103, 0.1);
`;

const WhyText = styled.div`
  h3 {
    font-family: "Lovelace", serif;
    font-size: 1.5rem;
    color: #63564e;
    margin: 0 0 10px;
    font-weight: 400;
  }

  p {
    color: #8e7479;
    line-height: 1.6;
    margin: 0;
  }
`;

// --- Animation Variants ---
const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

// --- Component ---
const Schulungen = () => {
  const trainings = [
    {
      title: "Einzeltechnik",
      badge: "Anfänger",
      badgeColor: "#9B8067",
      duration: "2 Tage",
      participants: "max. 4",
      certificate: "Zertifikat",
      includes: [
        "Theorie & Praxis",
        "Starterkit",
        "Übungsmaterial",
        "Nachbetreuung",
        "Anatomie Grundlagen",
        "Materialkunde",
      ],
      price: "700€",
    },
    {
      title: "Wispy-Technik",
      badge: "Exkurs",
      badgeColor: "#8E7479",
      duration: "1 Tag",
      participants: "max. 3",
      certificate: "Zertifikat",
      includes: [
        "Theorie & Praxis",
        "Wispy-Placement",
        "Textur-Techniken",
        "Soft & Mega Wispy",
        "Vorkenntnisse nötig",
      ],
      price: "400€",
    },
    {
      title: "Volumentechnik",
      badge: "Fortgeschritten",
      badgeColor: "#9B7A6B",
      duration: "2 Tage",
      participants: "max. 4",
      certificate: "Zertifikat",
      includes: [
        "Theorie & Praxis",
        "Starterkit",
        "Fächer-Techniken",
        "Isolierung",
        "Design-Aufbau",
      ],
      price: "700€",
    },
    {
      title: "Kombitechnik",
      badge: "Profi",
      badgeColor: "#63564E",
      duration: "3 Tage",
      participants: "max. 3",
      certificate: "Premium-Zertifikat",
      includes: [
        "Alle Techniken",
        "Premium Starterkit",
        "Hybrid-Techniken",
        "1:1 Betreuung",
        "VIP-Support",
      ],
      price: "1100€",
    },
  ];

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroImage
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        />
        <HeroOverlay />
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <HeroTitle>Schulungen</HeroTitle>
          <HeroSubtitle>
            Werde Teil unserer Community und lerne die Kunst der professionellen
            Wimpernverlängerung
          </HeroSubtitle>
          <HeroStats
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <StatItem>
              <StatNumber>50+</StatNumber>
              <StatLabel>Absolventinnen</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>4.9</StatNumber>
              <StatLabel>Bewertung</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>100%</StatNumber>
              <StatLabel>Praxis</StatLabel>
            </StatItem>
          </HeroStats>
        </HeroContent>
      </HeroSection>

      {/* What To Expect Section - NEW */}
      <ExpectSection>
        <ExpectContent
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariants}
        >
          <h2>WAS ERWARTET MICH?</h2>
          <p>
            In dieser Schulung lernst du alle theoretischen Grundlagen der
            Wimpernverlängerung sowie die sichere und professionelle Umsetzung
            in der Praxis. Du erhältst fundiertes Wissen zu Hygiene,
            Materialkunde, Styling und Technik und wirst Schritt für Schritt
            durch den kompletten Behandlungsablauf geführt – von der
            Vorbereitung bis zur Nachbehandlung und Kundenberatung.
          </p>
          <p>
            Ergänzt wird die Schulung durch wertvolle Einblicke in Business und
            Marketing.
          </p>
          <SupportNote
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <FiHeart />
            <span>
              Auch nach deiner Schulung steht dir meine Unterstützung bei Fragen
              und Hilfe zur Verfügung.
            </span>
          </SupportNote>
        </ExpectContent>

        <ExpectImageWrapper>
          <ExpectMainImage
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          />
          <ExpectAccentImage
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          />
        </ExpectImageWrapper>
      </ExpectSection>

      {/* Introduction Section */}
      <IntroSection>
        <IntroContent
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariants}
        >
          <h2>Deine Karriere als Wimpernstylistin</h2>
          <p>
            Bei uns lernst du nicht nur Techniken, sondern entwickelst dein
            eigenes Verständnis für Wimperndesign. Kleine Gruppen, viel Praxis
            und persönliche Betreuung stehen im Mittelpunkt.
          </p>
          <FeatureList>
            <Feature>
              <FiCheckCircle /> Kleine Gruppen
            </Feature>
            <Feature>
              <FiCheckCircle /> Praxis pur
            </Feature>
            <Feature>
              <FiCheckCircle /> Starterkit
            </Feature>
            <Feature>
              <FiCheckCircle /> Zertifikat
            </Feature>
          </FeatureList>
        </IntroContent>
        <IntroImage
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        />
      </IntroSection>

      {/* Trainings Section */}
      <TrainingsSection>
        <SectionHeader
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariants}
        >
          <h2>Unsere Schulungen</h2>
          <p>Wähle die Ausbildung, die zu dir passt</p>
        </SectionHeader>

        <TrainingsGrid>
          {trainings.map((training, index) => (
            <TrainingCard
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariants}
              transition={{ delay: index * 0.1 }}
            >
              <CardHeader>
                <CardTitle>{training.title}</CardTitle>
                <CardBadge color={training.badgeColor}>
                  {training.badge}
                </CardBadge>
              </CardHeader>

              <CardDetails>
                <Detail>
                  <FiClock />
                  <div>{training.duration}</div>
                  <span>Dauer</span>
                </Detail>
                <Detail>
                  <FiUsers />
                  <div>{training.participants}</div>
                  <span>Gruppe</span>
                </Detail>
                <Detail>
                  <FiAward />
                  <div>✓</div>
                  <span>Zertifikat</span>
                </Detail>
              </CardDetails>

              <IncludesList>
                <IncludesTitle>Inklusive:</IncludesTitle>
                <IncludesGrid>
                  {training.includes.map((item, idx) => (
                    <IncludeItem key={idx}>
                      <FiCheckCircle /> {item}
                    </IncludeItem>
                  ))}
                </IncludesGrid>
              </IncludesList>

              <CardFooter>
                <div>
                  <Price>{training.price}</Price>
                  <PriceNote>pro Person</PriceNote>
                </div>
                <BookButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiCalendar /> Termin
                </BookButton>
              </CardFooter>
            </TrainingCard>
          ))}
        </TrainingsGrid>
      </TrainingsSection>

      {/* Why Choose Us Section */}
      <WhySection>
        <WhyContent>
          <WhyImage
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          />
          <WhyList>
            <WhyItem
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariants}
            >
              <WhyIcon>
                <FiStar />
              </WhyIcon>
              <WhyText>
                <h3>Erfahrene Trainerinnen</h3>
                <p>
                  Lerne von den Besten mit jahrelanger Erfahrung und
                  erfolgreichen Absolventinnen
                </p>
              </WhyText>
            </WhyItem>
            <WhyItem
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariants}
              transition={{ delay: 0.1 }}
            >
              <WhyIcon>
                <FiHeart />
              </WhyIcon>
              <WhyText>
                <h3>Persönliche Atmosphäre</h3>
                <p>
                  Maximal 2 Teilnehmerinnen garantieren eine individuelle
                  Betreuung
                </p>
              </WhyText>
            </WhyItem>
            <WhyItem
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariants}
              transition={{ delay: 0.2 }}
            >
              <WhyIcon>
                <FiBookOpen />
              </WhyIcon>
              <WhyText>
                <h3>Umfangreiches Material</h3>
                <p>
                  Hochwertige Starterkits und Übungsmaterial für den perfekten
                  Start
                </p>
              </WhyText>
            </WhyItem>
          </WhyList>
        </WhyContent>
      </WhySection>
    </PageContainer>
  );
};

export default Schulungen;
